import { i18n } from '@lingui/core';
import { detect, fromNavigator } from '@lingui/detect-locale';
import { AxiosInstance } from 'axios';
import { setNumberFormatLocale } from '@/common/utils/formatNumber.ts';
import { setDateTimeFormatLocale } from '@/common/utils/formatDateTime.ts';

export const AVAILABLE_LOCALES = ['en', 'cs'] as const;

export type AvailableLocale = (typeof AVAILABLE_LOCALES)[number];

export function setupLocaleInterceptor(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.request.use((config) => {
    config.headers['Accept-Language'] = i18n.locale;
    return config;
  });
}

export async function switchLocale(locale: AvailableLocale) {
  try {
    const { messages } = await import(`../locales/${locale}/messages.po`);
    i18n.load(locale, messages);
    i18n.activate(locale);
    setNumberFormatLocale(locale);
    setDateTimeFormatLocale(locale);
  } catch (e) {
    console.error(`[i18n] Failed to load locale "${locale}":`, e);
  }
}

export async function setupI18n() {
  const detectedLocale = detect(fromNavigator());
  const locale = getAvailableLocale(detectedLocale);
  await switchLocale(locale);
}

function getAvailableLocale(locale: string | null): AvailableLocale {
  if (!locale) {
    return AVAILABLE_LOCALES[0];
  }

  // Split the locale into parts
  const parts = locale.split('-');

  // Try progressively shorter versions
  for (let i = parts.length; i > 0; i--) {
    const candidate = parts.slice(0, i).join('-');
    if (isAvailableLocale(candidate)) {
      return candidate;
    }
  }

  // No match found, return default
  return AVAILABLE_LOCALES[0];
}

function isAvailableLocale(locale: string): locale is AvailableLocale {
  return AVAILABLE_LOCALES.includes(locale as AvailableLocale);
}
